import {useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';

import {useQuery} from '@apollo/client/react';
import {setLoginInfo as setGlobalLoginInfo, clearLoginInfo} from '../../graphql/localState';
import {gql} from 'types/__generated__';
import type {LoginInfoQuery} from 'types/__generated__/graphql';

const LOGIN_INFO_QUERY = gql(`
  query LoginInfo {
    loginInfo {
      id
      user {
        id
        username
        firstName
        lastName
        email
      }
      organisationMemberships {
        organisationId
        name
      }
      currentOrganisation {
        id
        name
      }
      impersonatedBy
      isSystemAdmin
    }
  }
`);

/** The user this component hands back once the session is confirmed. */
type SignedInUser = NonNullable<NonNullable<LoginInfoQuery['loginInfo']>['user']>;

interface LoginInfoProps {
  setLoginInfo: (info: SignedInUser) => void;
}

export default function LoginInfo({setLoginInfo}: LoginInfoProps) {
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  const {data, loading, error} = useQuery(LOGIN_INFO_QUERY, {
    fetchPolicy: 'network-only', // Always fetch from network, ignore cache
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    console.log('LoginInfo state:', {loading, hasData: !!data, hasError: !!error});
    console.log('LoginInfo data:', JSON.stringify(data, null, 2));
    console.log('LoginInfo data.loginInfo:', data?.loginInfo);
    console.log('LoginInfo data.loginInfo?.user:', data?.loginInfo?.user);
    console.log('Auth token present:', !!localStorage.getItem('access_token'));
    if (error) {
      // Apollo 4 surfaces a single `error`; the old networkError/graphQLErrors pair is gone.
      console.error('LoginInfo GraphQL error:', error);
    }

    // If we have data
    if (!loading && data) {
      if (data.loginInfo && data.loginInfo.user) {
        // We have valid loginInfo with user data
        console.log('LoginInfo received:', data.loginInfo);

        // The admin app is for CWAY staff only
        if (data.loginInfo.isSystemAdmin) {
          console.log('User authenticated with admin permissions');
          setLoginInfo(data.loginInfo.user);
          // Also publish the full login info so pages outside this tree (e.g. UserDetailPage)
          // can see who is logged in and whether the session is impersonated.
          setGlobalLoginInfo((data as any).loginInfo);
          hasRedirected.current = false; // Reset redirect flag on success
        } else {
          // Authenticated but no admin permissions
          console.log('User authenticated but no admin permissions - redirecting to login');
          clearLoginInfo();
          if (!hasRedirected.current) {
            hasRedirected.current = true;
            navigate('/admin/auth/login-page', {replace: true});
          }
        }
      } else if (data.loginInfo === null) {
        // loginInfo is explicitly null - auth failed
        console.log('LoginInfo is null - redirecting to login');
        clearLoginInfo();
        if (!hasRedirected.current) {
          hasRedirected.current = true;
          navigate('/admin/auth/login-page', {replace: true});
        }
      }
    }
    // If we have an error (401/403 authentication failed)
    else if (!loading && error && !hasRedirected.current) {
      console.error('Authentication error:', error);
      hasRedirected.current = true;
      navigate('/admin/auth/login-page', {replace: true});
    }
  }, [data, loading, error, navigate, setLoginInfo]);

  // This component doesn't render anything
  return null;
}
